import React, { useCallback, useContext, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Input, InputWithLabel, InputWithLabelProps } from '@wix/design-system';
import { ThemedInputLabel } from '../ThemedComponents';
import classNames from 'classnames';
import s from '../InputField/InputField.scss';
import { PasswordInput } from '@wix/identity-fed-common';
import { PasswordInputProps } from '@wix/identity-fed-common/dist/types/password-input/components/password-input/PasswordInput';
import { AppContextProvider } from '../AppLoader';

export type CustomInputProps = InputWithLabelProps &
  PasswordInputProps & {
    inputWithLabel?: boolean;
    submitOnEnter?: boolean;
  };

// Fallback for inputs without a stable `name` (React 16 has no useId).
let customInputErrorIdCounter = 0;

// Standard visually-hidden style: kept in the a11y tree (announced / associable)
// but off-screen so it doesn't duplicate the visible WDS status message.
const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const CustomInputCore: React.ForwardRefRenderFunction<any, CustomInputProps> = (
  {
    onBlur,
    onFocus,
    status,
    onEnterPressed,
    border,
    inputWithLabel,
    label,
    submitOnEnter = true,
    className: propsClassName,
    ...props
  },
  ref,
) => {
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const {
    rootStore: {
      displayStore: { isMobile },
    },
  } = useContext(AppContextProvider);
  const InputElement = getInputElement(inputWithLabel, props.type);

  // WDS renders the status message visually but does not connect it to the
  // input for assistive tech. Associate + announce the error ourselves: a
  // visually-hidden role="alert" region carries the message, and the input
  // points at it via aria-describedby + is marked aria-invalid.
  const [generatedErrorId] = useState(
    () => `custom-input-error-${customInputErrorIdCounter++}`,
  );
  const errorId =
    typeof props.name === 'string' && props.name
      ? `${props.name}-error`
      : generatedErrorId;
  const hasError = status === 'error' && Boolean(props.statusMessage);
  // PasswordInput (third-party) doesn't forward arbitrary aria props to the
  // DOM; skip the attributes there (the live region still announces the error).
  const ariaErrorProps =
    hasError && props.type !== 'password'
      ? { ariaInvalid: true, ariaDescribedby: errorId }
      : {};

  const autoFocus = useMemo(() => {
    return props.autoFocus && !isMobile;
  }, [props.autoFocus, isMobile]);

  const onInputEnterPressed = useCallback(
    (e?: React.KeyboardEvent<HTMLInputElement>) => {
      e?.preventDefault();
      e && onEnterPressed?.(e);
      if (submitOnEnter) {
        const submitButton: any = document.querySelector('[type="submit"]');
        submitButton?.click();
        if (props?.statusMessage) {
          inputEl?.focus();
        }
      }
    },
    [onEnterPressed, submitOnEnter, inputEl, props?.statusMessage],
  );
  const isBottomLine = border === 'bottomLine';

  return (
    <>
      <InputElement
        // The only reason I do two class names for the same prediction is to override WSR css priority
        className={classNames(
          {
            [s.bottomLine]: isBottomLine,
            [s.priorityOnly]: isBottomLine,
          },
          propsClassName,
        )}
        inputRef={(ref) => setInputEl(ref)}
        {...props}
        ref={ref}
        onEnterPressed={(e) => {
          inputEl?.blur();
          onInputEnterPressed(e);
        }}
        status={status}
        {...ariaErrorProps}
        onFocus={onFocus}
        onBlur={onBlur}
        border={border}
        autoFocus={autoFocus}
        inputWithLabel={inputWithLabel}
        {...(inputWithLabel && label
          ? { label: <ThemedInputLabel>{label}</ThemedInputLabel> }
          : ({} as any))}
      />
      <span id={errorId} role="alert" style={visuallyHiddenStyle}>
        {hasError ? props.statusMessage : ''}
      </span>
    </>
  );
};

const getInputElement = (inputWithLabel?: boolean, type?: string) => {
  if (type === 'password') {
    return PasswordInput as any;
  }
  return inputWithLabel ? InputWithLabel : Input;
};

export const CustomInput = observer(React.forwardRef(CustomInputCore));
